import WarningOutlined from '@ant-design/icons/WarningOutlined'
import { Form, Input } from 'antd'
import { MainInfo } from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import {
  TYPE_TO_METHOD_TITLE,
  TYPE_TO_SHORT_NAME,
} from 'pages/reporting/shared/constants'
import { GenerateButton } from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoInspectionType,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

const { TextArea } = Input

/**
 * ScopeContainer - Wrapper for the scope form elements
 */
const ScopeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

/**
 * StyledTextArea - Enhanced textarea with consistent styling
 */
const StyledTextArea = styled(TextArea)`
  && {
    font-size: 0.875rem;
    line-height: 1.6;
    border-radius: 0.375rem;

    &.ant-input-status-warning {
      border-color: ${({ theme }) => theme.forecast.warning};
    }
  }
`

/**
 * ButtonRow - Container for action buttons
 */
const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
`

interface InspectionScopeProps {
  mainInfoData?: MainInfo
  geometricUnit: GeckoMeasurementUnit
  surfaceTemperature?: number
  temperatureUnit?: string
  material?: string
  scopesByScanType?: Record<string, string | undefined>
  initialScope?: string
  onChange: (scope: string) => void
  onGenerate: () => void
}

export const InspectionScope: React.FC<InspectionScopeProps> = ({
  geometricUnit,
  surfaceTemperature,
  temperatureUnit,
  material,
  scopesByScanType,
  initialScope,
  onChange,
  onGenerate,
}) => {
  const [form] = Form.useForm()
  const { uniqueScanTypes, formData, nominalsByInspection } = useRautReportOps()

  useEffect(() => {
    if (initialScope) {
      form.setFieldsValue({ inspectionScope: initialScope })
    }
  }, [form, initialScope])

  const nominalString = React.useMemo(() => {
    const nominals: GeckoMeasurement[] = Array.from(
      nominalsByInspection.values()
    ).flatMap((value) => {
      if (value instanceof GeckoMeasurementRange) {
        return [value.start, value.end]
      }
      return [value]
    })

    if (nominals.length === 0) {
      return '[Nominal]'
    } else {
      const range = GeckoMeasurementRange.CreateFromStartEnd(
        GeckoMeasurement.Min(...nominals),
        GeckoMeasurement.Max(...nominals)
      )
      if (GeckoMeasurement.Equivalent(range.start, range.end)) {
        return range.start.convertTo(geometricUnit).toString()
      } else {
        return range.convertTo(geometricUnit).toString()
      }
    }
  }, [geometricUnit, nominalsByInspection])

  const generateScopeText = useCallback(() => {
    const placeholderData = {
      scanTypes: uniqueScanTypes,
      asset: formData.mainInfo?.asset || '[Asset]',
      customerName: formData.mainInfo?.customerName || '[Customer Name]',
      location: formData.mainInfo?.location || '[Location]',
      methodCoverageScopes: scopesByScanType ?? {},
      material: material || '[Material]',
      // Format GeckoMeasurement value and unit
      nominalThickness: nominalString,
      surfaceTemperature: surfaceTemperature || '',
      temperatureUnit: temperatureUnit || 'Ambient',
    }

    let temperatureDescription = 'ambient'
    if (temperatureUnit !== 'Ambient' && surfaceTemperature) {
      temperatureDescription = `${surfaceTemperature}${
        temperatureUnit === 'Celsius' ? '°C' : '°F'
      }`
    }

    return `Gecko Robotics was contracted to perform ${placeholderData.scanTypes.map((scanType) => TYPE_TO_METHOD_TITLE[scanType]).join(', ')} on the ${placeholderData.asset} at the ${placeholderData.customerName} facility at ${placeholderData.location}.

${placeholderData.customerName} identified ${uniqueScanTypes.map((scanType) => `${placeholderData.methodCoverageScopes[scanType] ?? ''} for ${TYPE_TO_SHORT_NAME[scanType as GeckoInspectionType] ?? scanType.toUpperCase()}`).join(', ')}.

The material for the ${placeholderData.asset} is ${placeholderData.material} with a nominal thickness of ${placeholderData.nominalThickness}. The surface temperature was ${temperatureDescription} at the time of inspection.`
  }, [
    uniqueScanTypes,
    formData.mainInfo?.asset,
    formData.mainInfo?.customerName,
    formData.mainInfo?.location,
    scopesByScanType,
    material,
    nominalString,
    surfaceTemperature,
    temperatureUnit,
  ])

  const generateInspectionScope = () => {
    const scope = generateScopeText()
    form.setFieldsValue({ inspectionScope: scope })
    onChange(scope)
    onGenerate()
  }

  const isStale = useMemo(() => {
    const currentScope = formData.summaryPageData.inspectionScope
    if (!currentScope) return false

    const generatedScope = generateScopeText()
    return currentScope !== generatedScope
  }, [formData.summaryPageData.inspectionScope, generateScopeText])

  return (
    <ScopeContainer>
      <Form form={form} layout='vertical'>
        <Form.Item name='inspectionScope' style={{ marginBottom: 0 }}>
          <StyledTextArea
            rows={6}
            status={isStale ? 'warning' : undefined}
            placeholder='Click "Generate Inspection Scope" to create scope text based on the information above, or enter custom text here.'
            onChange={(e) => {
              const newValue = e.target.value
              form.setFieldsValue({ inspectionScope: newValue })
              onChange(newValue)
            }}
          />
        </Form.Item>
      </Form>
      <ButtonRow>
        <GenerateButton
          onClick={generateInspectionScope}
          icon={isStale ? <WarningOutlined /> : undefined}>
          {isStale ? 'Regenerate Scope (Values Changed)' : 'Generate Scope'}
        </GenerateButton>
      </ButtonRow>
    </ScopeContainer>
  )
}
